import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { RequestService } from '@shared/services/request/request.service';
import { BUSINESS_ENTITY, GROUP_TYPE, RECENT_TYPE } from '@main/enums';
import { IEntityItemId } from '@main/types';
import { FeatureToggleService } from '@main/shared/feature-toggle';
import { ChannelFilter } from '@main/services/entity-common.service.types';
import { EntityCommonService } from './entity-common.service';
import { EntityGroup, EntityItem, EntitySingle } from '../models';
import { EntityHelpers } from '../helpers/entity.helpers';
import { EntityKeyword } from '../models/EntityKeyword';
import { GetAccessLogsResponse, IAccessLog } from './recents.service.types';
import { KeywordsEntityService } from '../reports/keywords/services/keywords.entity.service';

@Injectable()
export class RecentsService {
    categoryEnabled = this.featureToggleService.isEnabled('category_report');
    brandsEnabled = this.featureToggleService.isEnabled('brand_report');

    constructor(
        private requestService: RequestService,
        private entityCommonService: EntityCommonService,
        private keywordsEntityService: KeywordsEntityService,
        private featureToggleService: FeatureToggleService,
    ) {}

    getLast(): Observable<EntityItem> {
        return this.getList(1, null, null, null, false).pipe(map((res: EntityItem[]) => res.length > 0 && res[0]));
    }

    getList(
        size = 10,
        entityTypes?: BUSINESS_ENTITY | BUSINESS_ENTITY[],
        recentType?: RECENT_TYPE,
        channel: ChannelFilter = null,
        includeDashboardEntities = true,
    ): Observable<EntityItem[]> {
        const url = `accesslogs/datapoints`;

        const params: Record<string, any> = {
            includeDashboardEntities,
            size,
        };

        if (entityTypes) {
            params.entityTypes = Array.isArray(entityTypes) ? entityTypes.join(',') : entityTypes;
        }

        if (recentType) {
            params.types = recentType;
        }

        return this.requestService.get<GetAccessLogsResponse>(url, { params }).pipe(
            map((response) => response?.elements),
            switchMap((result) => this.resolveItems(result, channel)),
            catchError(() => of([])),
        );
    }

    private resolveItems(recents: IAccessLog[], channel: ChannelFilter): Observable<EntityItem[]> {
        const idsByEntityType: Partial<Record<BUSINESS_ENTITY, IEntityItemId[]>> = {};

        Object.entries(BUSINESS_ENTITY).forEach(([_, value]) => {
            idsByEntityType[value] = [];
        });

        recents.forEach((recent) => {
            const {
                details: { entityType: recentEntityType, instances = [], categoryId, unsavedGroups },
            } = recent;
            const entityType =
                recentEntityType === BUSINESS_ENTITY.Benchmark ? BUSINESS_ENTITY.Advertiser : recentEntityType;

            const unsavedGroupsInstances = unsavedGroups?.map((group) => group.instances).flat() || [];

            idsByEntityType[entityType].push(...instances, ...unsavedGroupsInstances);

            if (recentEntityType === BUSINESS_ENTITY.Benchmark && categoryId) {
                idsByEntityType[BUSINESS_ENTITY.Category].push(categoryId);
            }
        });

        const resolvers = Object.entries(idsByEntityType)
            .map(([entityType, ids]) => {
                if (!ids.length) {
                    return;
                }

                if (entityType === BUSINESS_ENTITY.Category && !this.categoryEnabled) {
                    return;
                }

                const uniqIds = EntityHelpers.uniqIds(ids);

                if (entityType === BUSINESS_ENTITY.Keyword) {
                    return this.keywordsEntityService
                        .resolveEntitiesByIds<EntityKeyword>(uniqIds)
                        .pipe(catchError(() => of([])));
                }

                return this.entityCommonService
                    .resolveEntitiesByIds<EntitySingle>(uniqIds, entityType as BUSINESS_ENTITY, channel)
                    .pipe(catchError(() => of([])));
            })
            .filter(Boolean);

        const groupsIds = recents.map(({ details: { favoriteIds } }) => favoriteIds).flat();

        if (groupsIds.length) {
            resolvers.push(this.entityCommonService.resolveGroupsByIds(groupsIds));
        }

        if (!resolvers.length) {
            return of([]);
        }

        return forkJoin(resolvers).pipe(
            map((items) => recents.map((v, i) => this.mapRecentEntity(v, items.flat(), i)).filter(Boolean)),
        );
    }

    private mapRecentEntity(recent: IAccessLog, resolvedEntities: EntityItem[], recentIndex: number): EntityItem {
        const {
            details: {
                type,
                entityType: recentEntityType,
                instances = [],
                favoriteIds = [],
                categoryId,
                unsavedGroups = [],
            },
        } = recent;

        if (categoryId && !this.categoryEnabled) return;

        const isComparison = type === RECENT_TYPE.COMPARISON;
        const isCategoryBenchmarkComparison = recentEntityType === BUSINESS_ENTITY.Benchmark;
        const entityType = isCategoryBenchmarkComparison ? BUSINESS_ENTITY.Advertiser : recentEntityType;

        const recentCombinedIds = [...instances, ...favoriteIds, categoryId].filter(Boolean);

        const unsavedGroupsEntities = unsavedGroups.map((item, n) => {
            const groupInstances = item.instances.map((id) => resolvedEntities.find((v) => v.id === id));
            return EntityHelpers.createVirtualGroup(
                n,
                item.name,
                groupInstances,
                GROUP_TYPE.REGULAR,
                entityType,
                recent,
                recent.id,
            );
        });

        const entities = recentCombinedIds
            .map((id) => resolvedEntities.find((v) => v.id === id && v.entityType === entityType))
            .filter(Boolean)
            .concat(unsavedGroupsEntities);

        if (!entities.length) return;

        if (categoryId) {
            entities.unshift(
                resolvedEntities.find((v) => v.entityType === BUSINESS_ENTITY.Category && v.id === categoryId),
            );
        }

        if (isComparison && entities.length < 2 && !EntityHelpers.entitiesHasGroup(entities)) return;

        let recentEntity: EntityItem;

        /* wrapping in virtual group for few items  */

        if (entities.length === 1) {
            recentEntity = entities[0] as EntityItem;
        } else {
            const divider = isComparison ? 'vs' : '&';
            const title = entities
                .map((v) =>
                    isCategoryBenchmarkComparison && v.entityType === BUSINESS_ENTITY.Category
                        ? v.title
                        : v.fullTitleHtml,
                )
                .join(` <span class="gray">${divider}</span> `);

            recentEntity = EntityHelpers.createVirtualGroup(
                `recent_${recentIndex}`,
                title,
                entities,
                isComparison
                    ? isCategoryBenchmarkComparison
                        ? GROUP_TYPE.COMPARISON_CATEGORY_BENCHMARK
                        : GROUP_TYPE.COMPARISON
                    : GROUP_TYPE.REGULAR,
                entityType,
            );
            (recentEntity as EntityGroup).titleGenerated = true;
        }

        recentEntity[Symbol.for('isRecent')] = true;

        return recentEntity;
    }
}
